/**
 * Incautadores — catálogo (tabla maestinc).
 *
 * Los incautos solo se registran a nombre de un incautador activo de este
 * catálogo. Aquí se agregan, se corrigen nombre/teléfono y se desactivan
 * (no se borran: los incautos y pagos viejos guardan su cédula).
 * La cédula no se edita; si está mal, se desactiva y se crea otro.
 */
import React, { useEffect, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import InputAdornment from "@mui/material/InputAdornment";
import MenuItem from "@mui/material/MenuItem";
import AddIcon from "@mui/icons-material/Add";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import SearchIcon from "@mui/icons-material/Search";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import { useGlobalMDAlert } from "context";
import {
  FP,
  PageHeader,
  FpDataGrid,
  StatusPill,
  BarraVolver,
  fpInputSx,
} from "components/financepro";
import { useApi } from "lib/api";

// 000-0000000-0
export const formatCedula = (v) => {
  const d = String(v || "")
    .replace(/\D/g, "")
    .slice(0, 11);
  let out = d.slice(0, 3);
  if (d.length > 3) out += `-${d.slice(3, 10)}`;
  if (d.length > 10) out += `-${d.slice(10, 11)}`;
  return out;
};

const VACIO = {
  id: null,
  cedula: "",
  nombres: "",
  telefono: "",
  activo: "S",
  tipotarifa: "",
  tarifa: "",
};

// Tarifa del pago al incautador (Pagos a Incautadores la usa para sugerir
// el monto de cada incauto, con tope el valor del incauto).
const TARIFAS = [
  { id: "", label: "Sin tarifa (se paga el valor del incauto)" },
  { id: "F", label: "Monto fijo por incauto" },
  { id: "P", label: "Porcentaje del valor del incauto" },
];
const textoTarifa = (r) => {
  const t = Number(r.tarifa) || 0;
  if (r.tipotarifa === "F" && t > 0)
    return `RD$ ${t.toLocaleString("es-DO", { minimumFractionDigits: 2 })}`;
  if (r.tipotarifa === "P" && t > 0) return `${t}%`;
  return "—";
};

const btn = {
  textTransform: "none",
  borderRadius: 1.8,
  fontWeight: 700,
  boxShadow: "none",
};

export default function Incautadores({ embedded = false, onExit } = {}) {
  const { showAlert } = useGlobalMDAlert();
  const { post } = useApi();

  const [filas, setFilas] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [buscar, setBuscar] = useState("");
  const [verInactivos, setVerInactivos] = useState(false);

  const [editando, setEditando] = useState(null); // null = cerrado
  const [guardando, setGuardando] = useState(false);

  const cargar = async () => {
    setCargando(true);
    try {
      const { ok, data } = await post("/api/incautadores_listar", {});
      if (ok && Array.isArray(data.incautadores)) setFilas(data.incautadores);
      else showAlert({ message: data.Error || "No se pudo cargar el catálogo", color: "error" });
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => {
    cargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const visibles = useMemo(() => {
    const q = buscar.trim().toLowerCase();
    const qd = q.replace(/\D/g, "");
    return filas.filter((f) => {
      if (!verInactivos && f.activo !== "S") return false;
      if (!q) return true;
      return (
        (f.nombres || "").toLowerCase().includes(q) ||
        (qd && (f.cedula || "").replace(/\D/g, "").includes(qd)) ||
        (f.telefono || "").toLowerCase().includes(q)
      );
    });
  }, [filas, buscar, verInactivos]);

  const inactivos = filas.filter((f) => f.activo !== "S").length;

  const guardar = async () => {
    const e = editando;
    if (!e.id && e.cedula.replace(/\D/g, "").length !== 11) {
      showAlert({ message: "La cédula debe tener 11 dígitos", color: "warning" });
      return;
    }
    if (!e.nombres.trim()) {
      showAlert({ message: "Escriba el nombre del incautador", color: "warning" });
      return;
    }
    if (e.tipotarifa && !(Number(e.tarifa) > 0)) {
      showAlert({ message: "Indique el monto o porcentaje de la tarifa", color: "warning" });
      return;
    }
    setGuardando(true);
    try {
      const { ok, data } = await post("/api/incautador_guardar", {
        id: e.id,
        cedula: e.cedula,
        nombres: e.nombres,
        telefono: e.telefono,
        tipotarifa: e.tipotarifa || "",
        tarifa: e.tipotarifa ? e.tarifa : 0,
        activo: e.activo,
      });
      if (ok) {
        showAlert({ message: data.mensajeok || "Guardado", color: "success" });
        setEditando(null);
        cargar();
      } else {
        showAlert({ message: data.Error || "No se pudo guardar", color: "error" });
      }
    } finally {
      setGuardando(false);
    }
  };

  const cambiarActivo = async (fila) => {
    const { ok, data } = await post("/api/incautador_guardar", {
      id: fila.id,
      nombres: fila.nombres,
      telefono: fila.telefono,
      tipotarifa: fila.tipotarifa || "",
      tarifa: fila.tarifa || 0,
      activo: fila.activo === "S" ? "N" : "S",
    });
    if (ok) cargar();
    else showAlert({ message: data.Error || "No se pudo cambiar", color: "error" });
  };

  const columnas = [
    { field: "nombres", headerName: "Nombre", flex: 1.4, minWidth: 180 },
    { field: "cedula", headerName: "Cédula", flex: 0.9, minWidth: 130 },
    { field: "telefono", headerName: "Teléfono", flex: 0.8, minWidth: 120 },
    {
      field: "tarifa",
      headerName: "Tarifa",
      width: 120,
      valueGetter: (_, r) => textoTarifa(r),
    },
    {
      field: "incautos",
      headerName: "Incautos",
      type: "number",
      width: 95,
    },
    { field: "ultimo", headerName: "Último incauto", width: 130 },
    {
      field: "activo",
      headerName: "Estado",
      width: 120,
      renderCell: ({ row }) => (
        <Tooltip title={row.activo === "S" ? "Clic para desactivar" : "Clic para activar"}>
          <Box sx={{ cursor: "pointer" }} onClick={() => cambiarActivo(row)}>
            <StatusPill
              label={row.activo === "S" ? "Activo" : "Inactivo"}
              variant={row.activo === "S" ? "success" : "neutral"}
            />
          </Box>
        </Tooltip>
      ),
    },
    {
      field: "acciones",
      headerName: "",
      width: 60,
      sortable: false,
      renderCell: ({ row }) => (
        <IconButton size="small" onClick={() => setEditando({ ...VACIO, ...row })}>
          <EditOutlinedIcon fontSize="small" />
        </IconButton>
      ),
    },
  ];

  return (
    <Box component="main" sx={{ minHeight: "100vh", bgcolor: FP.color.surface }}>
      {embedded && <BarraVolver onExit={onExit} />}
      <PageHeader
        title="Incautadores"
        subtitle="Los incautos solo se registran a nombre de un incautador activo de esta lista"
      />
      <Box sx={{ p: { xs: 2, md: 3 } }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1.5}
          alignItems={{ xs: "stretch", sm: "center" }}
          sx={{ mb: 2 }}
        >
          <TextField
            size="small"
            placeholder="Buscar por nombre, cédula o teléfono"
            value={buscar}
            onChange={(e) => setBuscar(e.target.value)}
            sx={{ ...fpInputSx, flex: 1, maxWidth: { sm: 420 } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          <FormControlLabel
            control={
              <Switch checked={verInactivos} onChange={(e) => setVerInactivos(e.target.checked)} />
            }
            label={`Ver inactivos (${inactivos})`}
            sx={{ ml: { sm: 1 } }}
          />
          <Box sx={{ flex: 1 }} />
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setEditando({ ...VACIO })}
            sx={{
              ...btn,
              bgcolor: FP.color.accentBlue,
              color: "#fff",
              "&:hover": { bgcolor: FP.color.navySoft, color: "#fff" },
            }}
          >
            Agregar incautador
          </Button>
        </Stack>

        <Box sx={{ height: 560, bgcolor: FP.color.cardBg, borderRadius: 2.5 }}>
          <FpDataGrid
            rows={visibles}
            columns={columnas}
            loading={cargando}
            vacioIcono={BadgeOutlinedIcon}
            vacioTitulo="Sin incautadores"
            vacioSubtitulo='Agregue los incautadores con "Agregar incautador".'
            initialState={{ sorting: { sortModel: [{ field: "nombres", sort: "asc" }] } }}
          />
        </Box>
      </Box>

      <Dialog
        open={!!editando}
        onClose={() => !guardando && setEditando(null)}
        fullWidth
        maxWidth="xs"
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        {editando && (
          <>
            <DialogTitle sx={{ fontWeight: 800 }}>
              {editando.id ? "Editar incautador" : "Agregar incautador"}
            </DialogTitle>
            <DialogContent>
              <Grid container spacing={2} sx={{ pt: 1 }}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Cédula"
                    value={editando.cedula}
                    onChange={(e) =>
                      setEditando({ ...editando, cedula: formatCedula(e.target.value) })
                    }
                    placeholder="000-0000000-0"
                    InputProps={{ readOnly: !!editando.id }}
                    helperText={
                      editando.id
                        ? "La cédula no se cambia. Si está mal, desactívelo y cree otro."
                        : ""
                    }
                    sx={fpInputSx}
                    autoFocus={!editando.id}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Nombre"
                    value={editando.nombres}
                    onChange={(e) => setEditando({ ...editando, nombres: e.target.value })}
                    inputProps={{ maxLength: 45 }}
                    sx={fpInputSx}
                    autoFocus={!!editando.id}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Teléfono"
                    value={editando.telefono}
                    onChange={(e) => setEditando({ ...editando, telefono: e.target.value })}
                    inputProps={{ maxLength: 30 }}
                    sx={fpInputSx}
                  />
                </Grid>
                <Grid item xs={12} sm={editando.tipotarifa ? 7 : 12}>
                  <TextField
                    select
                    fullWidth
                    label="Tarifa del pago"
                    value={editando.tipotarifa || ""}
                    onChange={(e) => setEditando({ ...editando, tipotarifa: e.target.value })}
                    sx={fpInputSx}
                  >
                    {TARIFAS.map((t) => (
                      <MenuItem key={t.id || "ninguna"} value={t.id}>
                        {t.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                {editando.tipotarifa ? (
                  <Grid item xs={12} sm={5}>
                    <TextField
                      fullWidth
                      type="number"
                      label={editando.tipotarifa === "P" ? "Porcentaje" : "Monto"}
                      value={editando.tarifa}
                      onChange={(e) => setEditando({ ...editando, tarifa: e.target.value })}
                      InputProps={{
                        [editando.tipotarifa === "P" ? "endAdornment" : "startAdornment"]: (
                          <InputAdornment position={editando.tipotarifa === "P" ? "end" : "start"}>
                            {editando.tipotarifa === "P" ? "%" : "RD$"}
                          </InputAdornment>
                        ),
                      }}
                      sx={fpInputSx}
                    />
                  </Grid>
                ) : null}
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={editando.activo === "S"}
                        onChange={(e) =>
                          setEditando({ ...editando, activo: e.target.checked ? "S" : "N" })
                        }
                      />
                    }
                    label="Activo"
                  />
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
              <Button
                onClick={() => setEditando(null)}
                disabled={guardando}
                sx={{ ...btn, color: FP.color.textSecondary }}
              >
                Cancelar
              </Button>
              <Button
                variant="contained"
                onClick={guardar}
                disabled={guardando}
                sx={{
                  ...btn,
                  bgcolor: FP.color.accentBlue,
                  color: "#fff",
                  "&:hover": { bgcolor: FP.color.navySoft, color: "#fff" },
                }}
              >
                Guardar
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
}
